import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus,
  Search,
  FileText,
  Download,
  Trash2,
  File,
  FileSpreadsheet,
  Presentation,
  Image,
  Filter,
  ExternalLink,
  BookOpen,
  Eye,
  FolderOpen,
  CreditCard
} from 'lucide-react';
import { BusinessCardGenerator } from '../components/BusinessCardGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['EHR', 'RCM', 'Practice Management', 'CCM', 'RPM', 'APCM', 'AWV', 'General', 'Training'];
const FILE_TYPES = ['pdf', 'pptx', 'docx', 'xlsx', 'png', 'jpg', 'document', 'link'];

const getFileIcon = (type) => {
  switch (type) {
    case 'pdf': return <FileText className="w-6 h-6 text-red-500" />;
    case 'pptx': return <Presentation className="w-6 h-6 text-orange-500" />;
    case 'xlsx': return <FileSpreadsheet className="w-6 h-6 text-green-500" />;
    case 'docx': return <File className="w-6 h-6 text-blue-500" />;
    case 'document': return <BookOpen className="w-6 h-6 text-[#1B365D]" />;
    case 'link': return <ExternalLink className="w-6 h-6 text-[#0071E3]" />;
    case 'png':
    case 'jpg': return <Image className="w-6 h-6 text-purple-500" />;
    default: return <File className="w-6 h-6 text-slate-500" />;
  }
};

const Media = () => {
  const { getAuthHeaders, isAdmin, token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [showBusinessCard, setShowBusinessCard] = useState(false);
  const [newAsset, setNewAsset] = useState({
    name: '',
    description: '',
    file_url: '',
    file_type: 'pdf',
    category: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assets`, getAuthHeaders());
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/assets`, newAsset, getAuthHeaders());
      toast.success('Asset added');
      setShowCreateModal(false);
      setNewAsset({ name: '', description: '', file_url: '', file_type: 'pdf', category: '', tags: [] });
      fetchAssets();
    } catch (error) {
      toast.error('Failed to add asset');
    }
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await axios.delete(`${API_URL}/api/assets/${id}`, getAuthHeaders());
      toast.success('Asset deleted');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newAsset.tags.includes(tagInput.trim())) {
      setNewAsset({ ...newAsset, tags: [...newAsset.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewAsset({ ...newAsset, tags: newAsset.tags.filter(t => t !== tag) });
  };

  const filteredAssets = assets.filter(a => {
    const matchesSearch = !searchQuery || 
      a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || a.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6" data-testid="media-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Collateral</h1>
          <p className="text-slate-500 mt-1">Marketing assets, forms, and sales materials</p>
        </div>
        {isAdmin && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="add-asset-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add Asset</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAsset} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={newAsset.name} onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })} placeholder="EHR Product Brochure" required data-testid="asset-name" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={newAsset.description} onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })} placeholder="Brief description" data-testid="asset-description" />
                </div>
                <div className="space-y-2">
                  <Label>File URL *</Label>
                  <Input value={newAsset.file_url} onChange={(e) => setNewAsset({ ...newAsset, file_url: e.target.value })} placeholder="https://example.com/file.pdf" required data-testid="asset-url" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>File Type *</Label>
                    <Select value={newAsset.file_type} onValueChange={(v) => setNewAsset({ ...newAsset, file_type: v })}>
                      <SelectTrigger data-testid="asset-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FILE_TYPES.map(type => (<SelectItem key={type} value={type}>{type.toUpperCase()}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={newAsset.category} onValueChange={(v) => setNewAsset({ ...newAsset, category: v })}>
                      <SelectTrigger data-testid="asset-category"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
                    <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newAsset.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>{tag} x</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="submit-asset-btn">Add Asset</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Business Card Generator */}
      <Card className="border-0 shadow-apple bg-gradient-to-r from-[#1B365D] to-[#2a4a7a] text-white cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setShowBusinessCard(true)} data-testid="business-card-generator-cta">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-base">Business Card Generator</h3>
            <p className="text-sm text-white/70">Create personalized Enable Healthcare business cards — enter your details and download print-ready files</p>
          </div>
          <Button variant="secondary" size="sm" className="rounded-full bg-white text-[#1B365D] hover:bg-white/90 flex-shrink-0" data-testid="open-card-generator">
            Create Card
          </Button>
        </CardContent>
      </Card>
      <BusinessCardGenerator open={showBusinessCard} onOpenChange={setShowBusinessCard} />

      {/* Assets Section */}
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search assets..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-full" data-testid="search-assets" />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48 rounded-full" data-testid="media-category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

          {/* Assets Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-slate-500">Loading assets...</div>
            </div>
          ) : filteredAssets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAssets.map(asset => (
                <motion.div key={asset.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="border-0 shadow-apple hover:shadow-lg transition-shadow" data-testid={`asset-${asset.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                          {getFileIcon(asset.file_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{asset.name}</h3>
                          <p className="text-sm text-slate-500 line-clamp-2 mt-1">{asset.description || 'No description'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <Badge variant="outline">{asset.category}</Badge>
                        <Badge variant="secondary" className="uppercase text-xs">{asset.file_type}</Badge>
                      </div>
                      {asset.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {asset.tags.map(tag => (<Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                        {asset.file_type === 'document' && asset.content ? (
                          <Button variant="outline" className="flex-1 rounded-full" size="sm" onClick={() => setViewingDocument(asset)} data-testid={`view-doc-${asset.id}`}>
                            <Eye className="w-4 h-4 mr-2" /> Read
                          </Button>
                        ) : asset.file_type === 'link' ? (
                          <a href={asset.file_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button className="w-full rounded-full bg-[#0071E3] hover:bg-[#0071E3]/90" size="sm">
                              <ExternalLink className="w-4 h-4 mr-2" /> Visit Website
                            </Button>
                          </a>
                        ) : (
                          <>
                            <a href={asset.file_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                              <Button variant="outline" className="w-full rounded-full" size="sm">
                                <ExternalLink className="w-4 h-4 mr-2" /> Open
                              </Button>
                            </a>
                            <a href={asset.file_url} download>
                              <Button variant="outline" size="sm" className="rounded-full">
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                          </>
                        )}
                        {isAdmin && (
                          <Button variant="outline" size="sm" className="rounded-full text-red-500 hover:text-red-600" onClick={() => handleDeleteAsset(asset.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-apple">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">No assets found</h3>
                <p className="text-slate-500">
                  {searchQuery || categoryFilter !== 'all' ? 'Try adjusting your search or filter' : 'Marketing assets will appear here once added.'}
                </p>
              </CardContent>
            </Card>
          )}
      </div>

      {/* Document Viewer Dialog */}
      <Dialog open={!!viewingDocument} onOpenChange={(open) => !open && setViewingDocument(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#1B365D]" />
              {viewingDocument?.name}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[70vh] pr-4">
            <div className="prose prose-slate max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700 bg-slate-50 p-6 rounded-lg">
                {viewingDocument?.content}
              </pre>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Media;
